import { createAutosave } from './autosave';

/**
 * The lifecycle both details editors share (community/details/store.ts,
 * enterprise/details/store.ts): saving as the person types, reloading the
 * study's images while the server still works on one, refreshing the other
 * views once a burst of saves is over, and sending what is still waiting when
 * the editor closes. What a save sends, and how the rows look, stay each
 * edition's own.
 */
export interface DetailsEditorRuntime {
  /** A change to a target: saved once typing settles. */
  schedule: (key: string) => void;
  /** Save now: one target, or every target with a change waiting. */
  flush: (key?: string) => Promise<void>;
  /** A change is waiting or a save is running. */
  busy: () => boolean;
  /** Something changed on the server outside a save (a move): refresh once nothing is waiting. */
  changed: () => void;
  /** List the images now, and again while the server works on one. */
  load: () => Promise<void>;
  /** The editor is closing: stop polling; `beforeFlush` may queue a last save. What is waiting is sent. */
  close: (beforeFlush?: () => void) => void;
  closed: () => boolean;
}

export function createDetailsEditorRuntime<Image, Row>(o: {
  /** Saves one target. Resolves true when the server took something. */
  save: (key: string) => Promise<boolean>;
  /** The other views, loaded again (freshness.ts). */
  refresh: () => Promise<void>;
  /** Whether a change is waiting or a save is running, for the page's status line. */
  onPending: (pending: boolean) => void;
  fetchImages: () => Promise<Image[]>;
  /** The server's list, squared with the rows. */
  onImages: (images: Image[]) => void;
  /** The list could not be had: the study is gone (404), or the server was unreachable while nothing is listed. */
  onImagesError: (err: unknown) => boolean;
  rows: () => Row[] | null;
  isProcessing: (r: Row) => boolean;
  delayMs: number;
  pollMs: number;
}): DetailsEditorRuntime {
  let closed = false;
  let unrefreshed = false;
  let poll: ReturnType<typeof setTimeout> | undefined;

  const refreshIfIdle = () => {
    if (!unrefreshed || saver.busy()) return;
    unrefreshed = false;
    void o.refresh();
  };
  const saver = createAutosave(
    async (key) => {
      if (await o.save(key)) unrefreshed = true;
    },
    {
      delayMs: o.delayMs,
      // Once nothing is waiting: the other pages' copies are reloaded once, not per keystroke.
      onIdle: () => {
        if (!closed) o.onPending(false);
        refreshIfIdle();
      },
    },
  );

  const load = async (): Promise<void> => {
    if (poll) clearTimeout(poll);
    poll = undefined;
    try {
      const images = await o.fetchImages();
      if (closed) return;
      o.onImages(images);
    } catch (err) {
      if (closed) return;
      // Gone for good: no more asking.
      if (o.onImagesError(err)) return;
    }
    // While the server works on an image, its row is asked about again.
    const rows = o.rows();
    if (!closed && (!rows || rows.some(o.isProcessing))) poll = setTimeout(() => void load(), o.pollMs);
  };

  return {
    schedule: (key) => {
      o.onPending(true);
      saver.schedule(key);
    },
    flush: (key) => saver.flush(key),
    busy: () => saver.busy(),
    changed: () => {
      unrefreshed = true;
      refreshIfIdle();
    },
    load,
    close: (beforeFlush) => {
      if (closed) return;
      closed = true;
      if (poll) clearTimeout(poll);
      beforeFlush?.();
      // What was typed a moment ago still goes; the refresh follows it (onIdle).
      void saver.flush();
    },
    closed: () => closed,
  };
}
